import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

/**
 * Editor for a payer's Secondary Routing Rules.
 *
 * Each rule decides what the billing engine does automatically when a
 * primary EOB posts and THIS payer is the claim's secondary:
 *   standard        — bill secondary as normal
 *   skip_next_payer — skip; balance → next payer in sequence, else client account
 *   skip_write_off  — skip; balance → next payer in sequence, else written off
 * Rules fire in order; the first rule whose conditions all match wins.
 *
 * Backend contract: insurances.secondary_routing_rules: List[Rule] where
 * Rule = { id, action, primary_payer_id?, primary_payer_name?, balance_below?, notes? }
 */
const ACTION_OPTIONS = [
  { value: 'standard', label: 'Standard — bill secondary as normal' },
  { value: 'skip_next_payer', label: 'Skip — next payer in sequence, else client account' },
  { value: 'skip_write_off', label: 'Skip — next payer in sequence, else write off balance' },
];

export const SecondaryRoutingRulesEditor = ({ rules, onChange, payers = [] }) => {
  const list = Array.isArray(rules) ? rules : [];
  const tid = 'secondary-routing-rule';

  const update = (idx, patch) => {
    onChange(list.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const remove = (idx) => onChange(list.filter((_, i) => i !== idx));

  const add = () => {
    onChange([
      ...list,
      {
        id: (crypto.randomUUID && crypto.randomUUID()) || `rule-${Date.now()}`,
        action: 'skip_next_payer',
        primary_payer_id: null,
        primary_payer_name: null,
        balance_below: null,
        notes: '',
      },
    ]);
  };

  return (
    <div className="space-y-3" data-testid={`${tid}-editor`}>
      {list.length === 0 && (
        <p className="text-xs text-gray-500 italic" data-testid={`${tid}-empty`}>
          No routing rules. When this payer is secondary on a claim, it is billed normally.
        </p>
      )}

      {list.map((rule, idx) => (
        <div
          key={rule.id || idx}
          className="p-3 border border-gray-200 rounded-md bg-gray-50 space-y-2"
          data-testid={`${tid}-row-${idx}`}
        >
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <Label className="text-xs">When this payer is secondary:</Label>
              <Select
                value={rule.action || 'standard'}
                onValueChange={(v) => update(idx, { action: v })}
              >
                <SelectTrigger data-testid={`${tid}-action-${idx}`} className="mt-1">
                  <SelectValue placeholder="Select action" />
                </SelectTrigger>
                <SelectContent>
                  {ACTION_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => remove(idx)}
              data-testid={`${tid}-remove-${idx}`}
              className="mt-5 text-red-600 hover:bg-red-50"
              aria-label="Remove routing rule"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-gray-600">
                Only when primary payer is (optional)
              </Label>
              <Select
                value={rule.primary_payer_id || 'any'}
                onValueChange={(v) => {
                  if (v === 'any') {
                    update(idx, { primary_payer_id: null, primary_payer_name: null });
                  } else {
                    const p = payers.find((x) => x.id === v);
                    update(idx, { primary_payer_id: v, primary_payer_name: p?.name || null });
                  }
                }}
              >
                <SelectTrigger data-testid={`${tid}-primary-payer-${idx}`} className="mt-1">
                  <SelectValue placeholder="Any primary payer" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any primary payer</SelectItem>
                  {payers.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor={`${tid}-balance-${idx}`} className="text-xs text-gray-600">
                Only when remaining balance is below $ (optional)
              </Label>
              <Input
                id={`${tid}-balance-${idx}`}
                data-testid={`${tid}-balance-${idx}`}
                type="number"
                min="0"
                step="0.01"
                value={rule.balance_below ?? ''}
                onChange={(e) =>
                  update(idx, {
                    balance_below: e.target.value === '' ? null : parseFloat(e.target.value),
                  })
                }
                placeholder="e.g. 25.00 (blank = any amount)"
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <Label htmlFor={`${tid}-notes-${idx}`} className="text-xs text-gray-600">
              Notes (optional)
            </Label>
            <Input
              id={`${tid}-notes-${idx}`}
              data-testid={`${tid}-notes-${idx}`}
              value={rule.notes || ''}
              onChange={(e) => update(idx, { notes: e.target.value })}
              placeholder="e.g. MassHealth never pays under $25 after Medicare"
              className="text-sm mt-1"
            />
          </div>
        </div>
      ))}

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={add}
        data-testid={`${tid}-add`}
      >
        <Plus className="h-3 w-3 mr-1" /> Add routing rule
      </Button>
    </div>
  );
};

export default SecondaryRoutingRulesEditor;
